import * as THREE from "three";
import { BooleanResult, Polygon, Solid, Vector3 } from "opengeometry";
import { IShape } from "../../shapes/base-type";
import { ElementType } from "../base-type";
import { OPElement, OPGeometry, IPlaceable } from "../op-element";
import type { Vec3 } from "../../types";
import { degToRad, Y_UP } from "../../../../../utils/transform";
import { Opening } from "../openings/opening";
import {
  centroidXZ,
  clonePolygonProfile,
  rotatePointsXZ,
  validateAndNormalizeProfile,
  type PolygonProfile,
  type Point2,
} from "../../../../../utils/polygon";

export enum FloorMaterial {
  HARDWOOD = "HARDWOOD",
  TILE = "TILE",
  CARPET = "CARPET",
  LAMINATE = "LAMINATE",
  CONCRETE = "CONCRETE",
  OTHER = "OTHER",
}

/** Polygon profile for a Floor — alias of {@link PolygonProfile}. */
export type FloorProfile = PolygonProfile;

export interface FloorOptions {
  ogid?: string;
  labelName: string;
  type: ElementType.FLOOR;
  /** Polygon profile in the world XZ plane. `outerRing` is auto-closed and
   *  normalized to CW (the convention `opengeometry.Polygon` expects). Holes
   *  are auto-closed and normalized to CCW. */
  profile: FloorProfile;
  thickness: number;
  color: number;
  material: FloorMaterial;
  /** World Y of the bottom face of the floor. The profile is extruded upward
   *  by `thickness`. */
  elevation: number;
  /** ogids of openings whose `opening3D` Solids should be CSG-subtracted from
   *  the extruded floor at build time. Populated by `addOpening` /
   *  `removeOpening`. */
  openings: string[];
}

const DEFAULT_PROFILE: FloorProfile = {
  outerRing: [
    [-2, -1.5],
    [ 2, -1.5],
    [ 2,  1.5],
    [-2,  1.5],
  ],
};

function ringToVector3(ring: Point2[], y: number): Vector3[] {
  return ring.map(([x, z]) => new Vector3(x, y, z));
}

function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    if (Array.isArray(mesh.material)) mesh.material.forEach((m) => m.dispose());
    else if (mesh.material) (mesh.material as THREE.Material).dispose();
  });
  obj.removeFromParent();
}

export class Floor extends OPElement implements IShape, IPlaceable {
  ogType = ElementType.FLOOR;
  ogid: string;

  subElements2D: Map<string, OPGeometry> = new Map();
  subElements3D: Map<string, OPGeometry> = new Map();

  selected = false;
  edit = false;
  locked = false;

  private _floorProfileView = true;
  private _floorModelView = true;
  private _outlineEnabled = false;

  private _openings: Opening[] = [];
  private _openingHandlers: Map<string, () => void> = new Map();

  propertySet: FloorOptions = {
    labelName: "Floor",
    type: ElementType.FLOOR,
    profile: clonePolygonProfile(DEFAULT_PROFILE),
    thickness: 0.02,
    color: 0xe8d8b0,
    material: FloorMaterial.HARDWOOD,
    elevation: 0,
    openings: [],
  };

  constructor(config?: Partial<FloorOptions>) {
    super();
    this.ogid = config?.ogid ?? crypto.randomUUID();

    if (config) {
      this.propertySet = {
        ...this.propertySet,
        ...config,
        profile: config.profile
          ? clonePolygonProfile(config.profile)
          : clonePolygonProfile(this.propertySet.profile),
        openings: [...(config.openings ?? this.propertySet.openings)],
      };
    }
    this.propertySet.ogid = this.ogid;

    // Validate eagerly so the throw stack points at the caller's `new Floor(...)`.
    validateAndNormalizeProfile(this.propertySet.profile, "Floor");

    this.setOPGeometry();
  }

  get labelName() { return this.propertySet.labelName; }
  set labelName(value: string) { this.propertySet.labelName = value; }

  get profile(): FloorProfile { return clonePolygonProfile(this.propertySet.profile); }
  set profile(value: FloorProfile) {
    // Validate before mutating so propertySet stays consistent on bad input.
    validateAndNormalizeProfile(value, "Floor");
    this.propertySet.profile = clonePolygonProfile(value);
    this.setOPGeometry();
  }

  get floorThickness() { return this.propertySet.thickness; }
  set floorThickness(value: number) {
    this.propertySet.thickness = Math.max(0.005, value);
    this.setOPGeometry();
  }

  get floorColor() { return this.propertySet.color; }
  set floorColor(value: number) {
    this.propertySet.color = value;
    this.setOPMaterial();
  }

  get floorMaterial() { return this.propertySet.material; }
  set floorMaterial(value: FloorMaterial) { this.propertySet.material = value; }

  get elevation() { return this.propertySet.elevation; }
  set elevation(value: number) {
    this.propertySet.elevation = value;
    this.setOPGeometry();
  }

  move(delta: Vec3): void {
    const [dx, dy, dz] = delta;
    this.propertySet.profile.outerRing = this.propertySet.profile.outerRing.map(
      ([x, z]) => [x + dx, z + dz] as Point2,
    );
    this.propertySet.profile.holes = (this.propertySet.profile.holes ?? []).map((ring) =>
      ring.map(([x, z]) => [x + dx, z + dz] as Point2),
    );
    this.propertySet.elevation += dy;
    this.setOPGeometry();
  }

  rotate(angleDeg: number, axis: Vec3 = Y_UP, anchor?: Vec3): void {
    if (axis[0] !== 0 || axis[1] !== 1 || axis[2] !== 0) {
      console.warn("Floor.rotate: only Y-axis rotation is supported; ignoring axis override.");
    }
    const anchorXZ: Point2 = anchor
      ? [anchor[0], anchor[2]]
      : centroidXZ(this.propertySet.profile.outerRing);
    const rad = degToRad(angleDeg);

    this.propertySet.profile.outerRing = rotatePointsXZ(this.propertySet.profile.outerRing, rad, anchorXZ);
    this.propertySet.profile.holes = (this.propertySet.profile.holes ?? []).map((ring) =>
      rotatePointsXZ(ring, rad, anchorXZ),
    );
    this.setOPGeometry();
  }

  /** Attach an opening element whose `opening3D` Solid will be CSG-subtracted
   *  from the extruded floor. The caller is responsible for positioning the
   *  opening so that its 3D extent intersects the floor volume. Floors only
   *  cut in 3D — for plan-view holes, use `profile.holes`. */
  addOpening(openingElement: Opening): void {
    if (this._openings.some((o) => o.ogid === openingElement.ogid)) return;
    this._openings.push(openingElement);
    if (!this.propertySet.openings.includes(openingElement.ogid)) {
      this.propertySet.openings.push(openingElement.ogid);
    }
    const handler = () => this.resolveOpenings();
    this._openingHandlers.set(openingElement.ogid, handler);
    openingElement.onOpeningUpdated.add(handler);
    this.resolveOpenings();
  }

  removeOpening(ogid: string): void {
    const idx = this._openings.findIndex((o) => o.ogid === ogid);
    if (idx === -1) return;
    const opening = this._openings[idx];
    this._openings.splice(idx, 1);
    this.propertySet.openings = this.propertySet.openings.filter((id) => id !== ogid);
    const handler = this._openingHandlers.get(ogid);
    if (handler) {
      opening.onOpeningUpdated.remove(handler);
      this._openingHandlers.delete(ogid);
    }
    this.resolveOpenings();
  }

  getHostedOpenings(): Opening[] {
    return [...this._openings];
  }

  get outline() { return this._outlineEnabled; }
  set outline(value: boolean) {
    this._outlineEnabled = value;
    this.applyOutline();
  }

  get profileView() { return this._floorProfileView; }
  set profileView(value: boolean) {
    this._floorProfileView = value;
    this.syncViewVisibility();
  }

  get modelView() { return this._floorModelView; }
  set modelView(value: boolean) {
    this._floorModelView = value;
    this.syncViewVisibility();
  }

  setOPGeometry(): void {
    this.dispose();

    const normalized = validateAndNormalizeProfile(this.propertySet.profile, "Floor");
    const y = this.propertySet.elevation;
    const outerVerts = ringToVector3(normalized.outer, y);
    const holeVerts = normalized.holes.map((ring) => ringToVector3(ring, y));

    // Invisible seed polygon — extrusion source for the 3D solid.
    const seed = new Polygon({
      vertices: outerVerts,
      holes: holeVerts,
      color: this.propertySet.color,
    });
    seed.ogid = this.ogid + "-2d-base";
    seed.visible = false;
    this.subElements2D.set(seed.ogid, seed);
    this.add(seed);

    const extruded = seed.extrude(this.propertySet.thickness);
    extruded.ogid = this.ogid + "-3d-base";
    extruded.color = this.propertySet.color;
    this.subElements3D.set(extruded.ogid, extruded);
    this.add(extruded);

    // Visible plan-view polygon (separate Polygon so we can later add
    // opening-derived holes here without disturbing the extrusion seed).
    const plan = new Polygon({
      vertices: ringToVector3(normalized.outer, y),
      holes: normalized.holes.map((ring) => ringToVector3(ring, y)),
      color: this.propertySet.color,
    });
    plan.ogid = this.ogid + "-2d";
    this.subElements2D.set(plan.ogid, plan);
    this.add(plan);

    this.resolveOpenings();
    this.applyOutline();
    this.syncViewVisibility();
  }

  private resolveOpenings(): void {
    const resolvedKey = this.ogid + "-3d-resolved";
    const existing = this.subElements3D.get(resolvedKey);
    if (existing) {
      disposeObject(existing);
      this.subElements3D.delete(resolvedKey);
    }

    const base3D = this.subElements3D.get(this.ogid + "-3d-base") as Solid | undefined;
    const opening3DSolids = this._openings
      .map((o) => o.opening3D)
      .filter((s): s is Solid => Boolean(s));

    if (base3D && opening3DSolids.length > 0) {
      try {
        const result = base3D.subtract(opening3DSolids, {
          color: this.propertySet.color,
          outline: this._outlineEnabled,
        }) as BooleanResult;
        result.ogid = resolvedKey;
        this.subElements3D.set(resolvedKey, result);
        this.add(result);
      } catch (err) {
        console.error(
          `[Floor] 3D subtract failed for ${opening3DSolids.length} opening(s).`,
          "openings:", this._openings.map((o) => o.ogid),
          err,
        );
        throw err;
      }
    }

    opening3DSolids.forEach((s) => { s.visible = false; });

    this.applyOutline();
    this.syncViewVisibility();
  }

  private applyOutline(): void {
    for (const obj of this.subElements2D.values()) obj.outline = this._outlineEnabled;
    for (const obj of this.subElements3D.values()) obj.outline = this._outlineEnabled;
  }

  private syncViewVisibility(): void {
    const planKey = this.ogid + "-2d";
    const resolvedKey = this.ogid + "-3d-resolved";
    const hasResolved = this.subElements3D.has(resolvedKey);

    for (const [key, obj] of this.subElements2D.entries()) {
      // "-2d-base" is the extrusion seed, never shown.
      obj.visible = this._floorProfileView && key === planKey;
    }
    for (const [key, obj] of this.subElements3D.entries()) {
      obj.visible = this._floorModelView && (!hasResolved || key === resolvedKey);
    }
  }

  setOPMaterial(): void {
    for (const obj of this.subElements2D.values()) {
      if ("color" in obj && typeof (obj as { color?: unknown }).color === "number") {
        (obj as Polygon).color = this.propertySet.color;
      }
    }
    for (const obj of this.subElements3D.values()) {
      if ("color" in obj && typeof (obj as { color?: unknown }).color === "number") {
        (obj as Solid).color = this.propertySet.color;
      }
    }
  }

  getOPConfig(): FloorOptions { return this.propertySet; }

  setOPConfig(config: FloorOptions): void {
    if (config.profile) {
      // Validate before mutating so propertySet stays consistent on bad input.
      validateAndNormalizeProfile(config.profile, "Floor");
    }
    this.propertySet = {
      ...this.propertySet,
      ...config,
      profile: config.profile ? clonePolygonProfile(config.profile) : this.propertySet.profile,
      openings: [...(config.openings ?? this.propertySet.openings)],
    };
    this.setOPGeometry();
    this.setOPMaterial();
  }

  dispose(): void {
    for (const obj of this.subElements2D.values()) disposeObject(obj);
    for (const obj of this.subElements3D.values()) disposeObject(obj);
    this.subElements2D.clear();
    this.subElements3D.clear();
  }
}
